export type SettingsUser = {
  firstName: string;
  lastName: string;
  email: string;
};

export type Company = {
  name: string;
  vatId: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    postalCode: string;
    state: string;
    country: string;
  };
  enforceSso: boolean;
};

/**
 * Backend role tokens (`account_type`), per the 2026-06-15 contract (Alejandro).
 * "Auditor" maps to the ref's `auditor` token (see company_service header).
 */
export type AccountType = "owner" | "admin" | "integration" | "compliance" | "auditor";

export const ACCOUNT_TYPES: AccountType[] = [
  "owner",
  "admin",
  "integration",
  "compliance",
  "auditor",
];

/** UI label for each backend role token. */
export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  owner: "Owner",
  admin: "Admin",
  integration: "Integration",
  compliance: "Compliance",
  auditor: "Auditor",
};

/** Per-role permission summaries (Alejandro, 2026-06-15) shown as role-tab tooltips. */
export const ACCOUNT_TYPE_DESCRIPTIONS: Record<AccountType, string> = {
  owner:
    "Full control: manage admins and every member, plus all company, screening, and monitoring settings.",
  admin: "Full access: manage team members and roles, plus all screening and monitoring.",
  integration: "API and integration access only.",
  compliance: "Run screening and monitoring. Cannot manage team members or company settings.",
  auditor:
    "Read-only: view screening and monitoring results. Cannot run screenings or change settings.",
};

/** Map a backend `account_type` token to its UI label (falls back to the raw token). */
export function accountTypeLabel(token: string): string {
  return ACCOUNT_TYPE_LABELS[token as AccountType] ?? token;
}

/** Roles allowed to administer the team (invite, edit roles, remove members). */
export const MANAGER_ROLES: AccountType[] = ["owner", "admin"];

/**
 * Roles an actor is allowed to assign, strictly below their own rank in
 * `ACCOUNT_TYPES` — an Owner can't create another Owner, an Admin can't
 * create another Admin, etc. `keepRole` (the member's current role, when
 * editing) is always included so an existing assignment stays visible/selected
 * even if it wouldn't be freshly assignable by this actor.
 */
export function getAssignableRoles(actorType: AccountType, keepRole?: AccountType): AccountType[] {
  const actorRank = ACCOUNT_TYPES.indexOf(actorType);
  return ACCOUNT_TYPES.filter((t, i) => i > actorRank || t === keepRole);
}

export type ScreeningConfig = {
  id: string;
  name: string;
  watchlists: string[];
  confidenceScore: number;
  entityType: "Individual" | "Organization" | "Vessel" | "Aircraft" | "Country" | null;
  countries: string[];
  dob: string;
  adverseMediaConfidenceScore: number;
  adverseMediaKeywords: string[];
  /** `null` = strictly defined in the search; `[]` = worldwide. */
  adverseMediaCountries: string[] | null;
  adverseMediaLanguages: string[];
};

export const MOCK_SCREENING_CONFIGS: ScreeningConfig[] = [
  {
    id: "config-1",
    name: "Config 1",
    watchlists: [],
    confidenceScore: 88,
    entityType: null,
    countries: [],
    dob: "",
    adverseMediaConfidenceScore: 88,
    adverseMediaKeywords: [],
    adverseMediaCountries: [],
    adverseMediaLanguages: [],
  },
];

export type SsoProviderId = "github" | "microsoft" | "google" | "okta";

export type SsoProvider = {
  id: SsoProviderId;
  name: string;
  description: string;
  enterprise?: boolean;
  connected: boolean;
};

export const MOCK_USER: SettingsUser = {
  firstName: "Jonathan",
  lastName: "Müller",
  email: "jonathan@sanctions.io",
};

export const MOCK_COMPANY: Company = {
  name: "Acme Compliance GmbH",
  vatId: "DE123456789",
  address: {
    line1: "Friedrichstraße 68",
    line2: "",
    city: "Berlin",
    postalCode: "10117",
    state: "Berlin",
    country: "Germany",
  },
  enforceSso: false,
};

export const MOCK_SSO_PROVIDERS: SsoProvider[] = [
  {
    id: "github",
    name: "GitHub",
    description: "Sign in with GitHub OAuth.",
    connected: false,
  },
  {
    id: "microsoft",
    name: "Microsoft",
    description: "Sign in with Microsoft Entra ID / Azure AD.",
    connected: true,
  },
  {
    id: "google",
    name: "Google",
    description: "Sign in with Google Workspace.",
    connected: false,
  },
  {
    id: "okta",
    name: "Okta",
    description: "SAML SSO with Okta.",
    enterprise: true,
    connected: false,
  },
];
